import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { logout } from './authSlice';
import type { AppDispatch, RootState } from '../store/store';
import { apiUrl } from '../config/api';

export interface TimeEntry {
  _id: string;
  userId: string;
  startTime: string;
  endTime?: string;
  pausedDuration: number;
  pauseStart?: string;
  isPaused: boolean;
  breakMinutes?: number;
  description?: string;
  type: 'work' | 'sick' | 'vacation';
  adminEditedAt?: string;
  adminEditedBy?: string | { _id: string; username?: string };
  adminEditComment?: string;
  createdAt?: string;
  updatedAt?: string;
}

export interface TimeEntryState {
  entries: TimeEntry[];
  activeEntry?: TimeEntry | null;
  loading: boolean;
  error?: string;
}

const initialState: TimeEntryState = {
  entries: [],
  activeEntry: null,
  loading: false,
  error: undefined,
};

const upsertEntry = (entries: TimeEntry[], entry: TimeEntry) => {
  const idx = entries.findIndex((e) => e._id === entry._id);
  if (idx === -1) {
    entries.push(entry);
  } else {
    entries[idx] = entry;
  }
};

// ✅ Einträge laden
export const fetchEntries = createAsyncThunk<
  TimeEntry[],
  { filter: string; date: string; userId?: string },
  { state: RootState; dispatch: AppDispatch }
>('timeEntries/fetch', async ({ filter, date, userId }, { getState, dispatch }) => {
  const token = getState().auth.token;
  const url = new URL(apiUrl('/api/time-entries'));
  url.searchParams.append('filter', filter);
  url.searchParams.append('date', date);
  if (userId) url.searchParams.append('userId', userId);

  const res = await fetch(url.toString(), {
    headers: { Authorization: `Bearer ${token}` },
  });

  if (res.status === 401) {
    dispatch(logout());
    throw new Error('Token abgelaufen, bitte neu anmelden.');
  }

  if (!res.ok) throw new Error('Fehler beim Laden der Einträge');
  return res.json();
});

export const fetchActiveEntryForToday = createAsyncThunk<
  TimeEntry | null,
  string,
  { state: RootState; dispatch: AppDispatch }
>('timeEntries/fetchActiveForToday', async (userId, { getState, dispatch }) => {
  const token = getState().auth.token;
  const today = new Date().toLocaleDateString('sv-SE');
  const url = new URL(apiUrl('/api/time-entries'));
  url.searchParams.append('filter', 'day');
  url.searchParams.append('date', today);
  url.searchParams.append('userId', userId);

  const res = await fetch(url.toString(), {
    headers: { Authorization: `Bearer ${token}` },
  });

  if (res.status === 401) {
    dispatch(logout());
    throw new Error('Token abgelaufen, bitte neu anmelden.');
  }

  if (!res.ok) throw new Error('Fehler beim Laden des aktiven Eintrags');

  const entries: TimeEntry[] = await res.json();
  return entries.find((entry) => !entry.endTime) || null;
});

// ✅ Neuen Arbeitseintrag anlegen
export const createEntry = createAsyncThunk<
  TimeEntry,
  { userId: string; startTime: string; endTime: string; pausedDuration: number },
  { state: RootState; dispatch: AppDispatch }
>('timeEntries/create', async (entryData, { getState, dispatch }) => {
  const token = getState().auth.token;
  const res = await fetch(apiUrl('/api/time-entries'), {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({
      ...entryData,
      isPaused: false,
      type: 'work',
    }),
  });

  if (res.status === 401) {
    dispatch(logout());
    throw new Error('Token abgelaufen, bitte neu anmelden.');
  }

  if (!res.ok) throw new Error('Fehler beim Erstellen');
  return res.json();
});

// ✅ Kranktage anlegen
export const createSickDay = createAsyncThunk<
  any,
  { userId: string; startDate: string; endDate: string; dayFraction?: number },
  { state: RootState; dispatch: AppDispatch }
>('timeEntries/createSickDay', async (data, { getState, dispatch }) => {
  const token = getState().auth.token;
  const res = await fetch(apiUrl('/api/time-entries/sick'), {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(data),
  });

  if (res.status === 401) {
    dispatch(logout());
    throw new Error('Token abgelaufen, bitte neu anmelden.');
  }

  if (!res.ok) throw new Error('Fehler beim Erstellen der Krankheitstage');
  return res.json();
});

// ✅ Eintrag bearbeiten
export const editEntry = createAsyncThunk<
  TimeEntry,
  { id: string; startTime: string; endTime: string; pausedDuration: number; adminEditComment?: string },
  { state: RootState; dispatch: AppDispatch }
>('timeEntries/edit', async ({ id, startTime, endTime, pausedDuration, adminEditComment }, { getState, dispatch }) => {
  const token = getState().auth.token;
  const res = await fetch(apiUrl(`/api/time-entries/${id}`), {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({ startTime, endTime, pausedDuration, adminEditComment }),
  });

  if (res.status === 401) {
    dispatch(logout());
    throw new Error('Token abgelaufen, bitte neu anmelden.');
  }

  if (!res.ok) throw new Error('Fehler beim Bearbeiten');
  return res.json();
});

export const createTimeEntryChangeRequest = createAsyncThunk<
  any,
  { entryId: string; startTime: string; endTime: string; pausedDuration: number; comment?: string },
  { state: RootState; dispatch: AppDispatch }
>('timeEntryChangeRequests/create', async (data, { getState, dispatch }) => {
  const token = getState().auth.token;
  const res = await fetch(apiUrl('/api/time-entry-change-requests'), {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(data),
  });

  if (res.status === 401) {
    dispatch(logout());
    throw new Error('Token abgelaufen, bitte neu anmelden.');
  }

  if (!res.ok) throw new Error('Fehler beim Erstellen des Änderungsantrags');
  return res.json();
});

// ✅ Eintrag löschen
export const deleteEntry = createAsyncThunk<
  { id: string },
  string,
  { state: RootState; dispatch: AppDispatch }
>('timeEntries/delete', async (id, { getState, dispatch }) => {
  const token = getState().auth.token;
  const res = await fetch(apiUrl(`/api/time-entries/${id}`), {
    method: 'DELETE',
    headers: { Authorization: `Bearer ${token}` },
  });

  if (res.status === 401) {
    dispatch(logout());
    throw new Error('Token abgelaufen, bitte neu anmelden.');
  }

  if (!res.ok) throw new Error('Fehler beim Löschen');
  return { id };
});

// ✅ Start
export const startEntry = createAsyncThunk<
  TimeEntry,
  void,
  { state: RootState; dispatch: AppDispatch }
>('timeEntries/start', async (_, { getState, dispatch }) => {
  const token = getState().auth.token;

  const res = await fetch(apiUrl('/api/time-entries'), {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({
      startTime: new Date().toISOString(),
      pausedDuration: 0,
      isPaused: false,
      type: 'work',
    }),
  });

  if (res.status === 401) {
    dispatch(logout());
    throw new Error('Token abgelaufen, bitte neu anmelden.');
  }

  if (!res.ok) throw new Error('Fehler beim Starten');
  return res.json();
});

// ✅ Pause
export const pauseEntry = createAsyncThunk<
  TimeEntry,
  string,
  { state: RootState; dispatch: AppDispatch }
>('timeEntries/pause', async (id, { getState, dispatch }) => {
  const token = getState().auth.token;

  const res = await fetch(apiUrl(`/api/time-entries/${id}/pause`), {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
  });

  if (res.status === 401) {
    dispatch(logout());
    throw new Error('Token abgelaufen');
  }

  if (!res.ok) throw new Error('Fehler beim Pausieren');
  return res.json();
});

// ✅ Stop
export const stopEntry = createAsyncThunk<
  TimeEntry,
  string,
  { state: RootState; dispatch: AppDispatch }
>('timeEntries/stop', async (id, { getState, dispatch }) => {
  const token = getState().auth.token;

  const res = await fetch(apiUrl(`/api/time-entries/${id}/stop`), {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
  });

  if (res.status === 401) {
    dispatch(logout());
    throw new Error('Token abgelaufen');
  }

  if (!res.ok) throw new Error('Fehler beim Stoppen');
  return res.json();
});

const timeEntrySlice = createSlice({
  name: 'timeEntries',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchEntries.pending, (state) => {
        state.loading = true;
        state.error = undefined;
      })
      .addCase(fetchEntries.fulfilled, (state, action) => {
        state.loading = false;
        state.entries = action.payload;
      })
      .addCase(fetchEntries.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
      })
      .addCase(fetchActiveEntryForToday.fulfilled, (state, action) => {
        state.activeEntry = action.payload;
        if (action.payload) {
          upsertEntry(state.entries, action.payload);
        }
      })
      .addCase(createEntry.fulfilled, (state, action) => {
        state.entries.push(action.payload);
      })
      .addCase(editEntry.fulfilled, (state, action) => {
        const idx = state.entries.findIndex((e) => e._id === action.payload._id);
        if (idx !== -1) state.entries[idx] = action.payload;
      })
      .addCase(deleteEntry.fulfilled, (state, action) => {
        state.entries = state.entries.filter((e) => e._id !== action.payload.id);
      })

      // 🔥 FIX START
      .addCase(startEntry.fulfilled, (state, action) => {
        state.activeEntry = action.payload;
        upsertEntry(state.entries, action.payload);
      })

      .addCase(pauseEntry.fulfilled, (state, action) => {
        upsertEntry(state.entries, action.payload);

        if (state.activeEntry?._id === action.payload._id) {
          state.activeEntry = action.payload;
        }
      })

      // 🔥 FIX STOP
      .addCase(stopEntry.fulfilled, (state, action) => {
        state.activeEntry = null;
        upsertEntry(state.entries, action.payload);
      });
  },
});

export default timeEntrySlice.reducer;
